"use client";

import { useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Button,
  ColorField,
  Input,
  Spinner,
  Stepper,
  Textarea,
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/ui";
import { useOnboarding } from "@/lib/onboarding/OnboardingContext";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import useMutateRecruiter from "@/lib/user/useMutateRecruiter";
import { isUsableOnWhite } from "@/lib/onboarding/brandColors";
import CommunicationStylePanel from "./CommunicationStylePanel";
import styles from "./OnboardingModal.module.css";

interface LogoCandidate {
  url: string;
  source?: string;
}

interface OnboardingForm {
  company_name: string;
  company_summary: string;
  about_me: string;
  communication_style: string;
  logo_url: string;
  logo_source: string;
  primary_color: string;
  secondary_color: string;
}

const EMPTY_FORM: OnboardingForm = {
  company_name: "",
  company_summary: "",
  about_me: "",
  communication_style: "",
  logo_url: "",
  logo_source: "",
  primary_color: "",
  secondary_color: "",
};

const STEPS = [
  { id: "company", label: "Your company" },
  { id: "you", label: "About you" },
];

/** Human-readable name for where a logo candidate came from. */
const SOURCE_LABELS: Record<string, string> = {
  website: "From your website",
  "logo.dev": "From logo.dev",
  "logo.dev:brandmark": "Your brand lockup",
  favicon: "Your site favicon",
  "og:image": "Your social share image",
  upload: "Uploaded by you",
};

function sourceLabel(source?: string): string {
  return (source && SOURCE_LABELS[source]) || "Alternative logo";
}

interface PaletteRowProps {
  colors: string[];
  active: string;
  disabled: boolean;
  onPick: (color: string) => void;
}

/**
 * The brand's remaining dominant colours as one-click chips. Rendered under
 * each colour field so it is unambiguous which one a chip will fill.
 */
function PaletteRow({ colors, active, disabled, onPick }: PaletteRowProps) {
  const options = colors.filter(
    (color) => color.toLowerCase() !== active.trim().toLowerCase(),
  );

  if (options.length === 0) return null;

  return (
    <div className={styles.paletteStrip}>
      {options.map((color) => (
        <button
          key={color}
          type="button"
          className={styles.paletteChip}
          disabled={disabled}
          onClick={() => onPick(color)}
        >
          <span
            className={styles.paletteSwatch}
            style={{ backgroundColor: color }}
          />
          {color}
        </button>
      ))}
    </div>
  );
}

/**
 * Review modal for the gathered onboarding profile, split into two steps:
 * company details first, then the recruiter's own voice. Every field is
 * editable; "Looks great" applies the confirmed values to the account, "Review
 * later" dismisses (leaving the CTA), and "Run onboarding again" confirms the
 * domain first, then re-researches.
 */
export default function OnboardingModal() {
  const { modalOpen, data, applying, apply, reviewLater, openDomainPrompt } =
    useOnboarding();
  const { recruiter } = useRecruiterDetails();
  const { mutation: recruiterMutation } = useMutateRecruiter();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<OnboardingForm>(EMPTY_FORM);
  const [uploaded, setUploaded] = useState<LogoCandidate[]>([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Seed the form from the gathered findings whenever the dialog opens (or the
  // findings are replaced by a re-run). Adjusting state during render rather
  // than in an effect avoids a throwaway pass rendering the previous values.
  const [seed, setSeed] = useState<{ open: boolean; data: unknown }>({
    open: false,
    data: undefined,
  });
  if (modalOpen !== seed.open || (modalOpen && seed.data !== data)) {
    setSeed({ open: modalOpen, data });
    if (modalOpen) {
      const seeded = { ...EMPTY_FORM, ...(data ?? {}) };
      setForm({
        ...seeded,
        // Research only suggests colours when logo.dev knew the brand. Fall
        // back to whatever the recruiter's theme already has so the pickers
        // always show the colour that is actually in effect.
        primary_color: seeded.primary_color || recruiter?.primary_color || "",
        secondary_color:
          seeded.secondary_color || recruiter?.secondary_color || "",
      });
      setUploaded([]);
      setStep(0);
    }
  }

  const handleChange =
    (field: keyof OnboardingForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleTextareaChange =
    (field: keyof OnboardingForm) => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleColorChange =
    (field: "primary_color" | "secondary_color") => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const selectLogo = (candidate: LogoCandidate) => {
    setForm((prev) => ({
      ...prev,
      logo_url: candidate.url,
      logo_source: candidate.source ?? "",
    }));
  };

  const handleLogoFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !recruiter?.uuid) return;

    setLogoUploading(true);
    recruiterMutation.mutate(
      { id: recruiter.uuid, logo: file },
      {
        onSuccess: (res: any) => {
          const url = res?.data?.logo_url;
          if (url) {
            // Keep the upload in the strip so switching away from it isn't a
            // one-way door — the user can come back without re-uploading.
            setUploaded((prev) => [{ url, source: "upload" }, ...prev]);
            selectLogo({ url, source: "upload" });
          }
          setLogoUploading(false);
        },
        onError: () => {
          toast.error("Could not upload logo");
          setLogoUploading(false);
        },
      },
    );
  };

  const handleApply = async () => {
    try {
      // Merge edits over the full gathered payload so pass-through fields
      // (location, recruiting focus, sources, etc.) are preserved on apply.
      await apply({ ...(data ?? {}), ...form });
      toast.success("Your account has been set up");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Could not apply your onboarding details",
      );
    }
  };

  const busy = applying || logoUploading;
  const onFirstStep = step === 0;

  const researched: LogoCandidate[] = Array.isArray(data?.logo_candidates)
    ? data.logo_candidates
    : [];
  const alternatives = [...uploaded, ...researched].filter(
    (candidate, index, all) =>
      candidate?.url &&
      candidate.url !== form.logo_url &&
      all.findIndex((c) => c.url === candidate.url) === index,
  );

  // Only offer colours the API will actually accept — ContrastWithWhite rejects
  // anything too pale to read on white, so a rejected swatch would 422 on save.
  const palette: string[] = Array.isArray(data?.brand_colors)
    ? data.brand_colors.filter(
        (color: unknown): color is string =>
          typeof color === "string" && isUsableOnWhite(color),
      )
    : [];

  return (
    <Dialog
      open={modalOpen}
      onOpenChange={(open: boolean) => {
        if (!open) reviewLater();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader className={styles.header}>
            <DialogTitle>Review your account setup</DialogTitle>
            <Stepper
              className={styles.stepper}
              steps={STEPS}
              activeStep={step}
              onStepChange={busy ? undefined : setStep}
            />
          </DialogHeader>

          <DialogBody>
            {onFirstStep ? (
              <>
                <p className={styles.intro}>
                  We researched your business to set up your account. Check the
                  details below and edit anything that isn&apos;t quite right.
                </p>

                <div className={styles.fields}>
                  <div className={styles.logoRow}>
                    <div className={styles.logoPreview}>
                      {logoUploading ? (
                        <Spinner size="md" />
                      ) : form.logo_url ? (
                        <img src={form.logo_url} alt="Company logo" />
                      ) : (
                        <span className={styles.logoEmpty}>No logo</span>
                      )}
                    </div>

                    <div className={styles.uploadLabel}>
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {form.logo_url ? "Replace logo" : "Upload logo"}
                      </Button>
                      <input
                        ref={fileInputRef}
                        className={styles.hiddenInput}
                        accept="image/*"
                        type="file"
                        disabled={busy}
                        onChange={handleLogoFile}
                      />
                    </div>
                  </div>

                  {alternatives.length > 0 && (
                    <div className={styles.alternatives}>
                      <p className={styles.alternativesLabel}>
                        Not right? Pick another logo we found:
                      </p>
                      <div className={styles.alternativesStrip}>
                        {alternatives.map((candidate) => (
                          <button
                            key={candidate.url}
                            type="button"
                            className={styles.alternative}
                            title={sourceLabel(candidate.source)}
                            aria-label={sourceLabel(candidate.source)}
                            disabled={busy}
                            onClick={() => selectLogo(candidate)}
                          >
                            <img src={candidate.url} alt="" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className={styles.colors}>
                    <p className={styles.alternativesLabel}>
                      {palette.length > 0
                        ? "Accent colours we found in your brand"
                        : "Accent colours"}
                    </p>
                    <p className={styles.colorsHint}>
                      Used for design elements, buttons and links on the float
                      and job pages you send to contacts. You can change these
                      any time in Settings.
                    </p>

                    <div className={styles.colorFields}>
                      <div className={styles.colorFieldGroup}>
                        <ColorField
                          label="Primary accent"
                          value={form.primary_color}
                          onChange={handleColorChange("primary_color")}
                          disabled={busy}
                        />
                        <PaletteRow
                          colors={palette}
                          active={form.primary_color}
                          disabled={busy}
                          onPick={handleColorChange("primary_color")}
                        />
                      </div>

                      <div className={styles.colorFieldGroup}>
                        <ColorField
                          label="Secondary accent"
                          value={form.secondary_color}
                          onChange={handleColorChange("secondary_color")}
                          disabled={busy}
                        />
                        <PaletteRow
                          colors={palette}
                          active={form.secondary_color}
                          disabled={busy}
                          onPick={handleColorChange("secondary_color")}
                        />
                      </div>
                    </div>
                  </div>

                  <Input
                    label="Company name"
                    value={form.company_name}
                    onChange={handleChange("company_name")}
                    fullWidth
                    required
                  />

                  <Textarea
                    label="About the company"
                    value={form.company_summary}
                    onChange={handleTextareaChange("company_summary")}
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <>
                <p className={styles.intro}>
                  This is how we&apos;ll introduce you and match your tone when
                  drafting outreach on your behalf.
                </p>

                <div className={styles.fields}>
                  <Textarea
                    label="About me"
                    value={form.about_me}
                    onChange={handleTextareaChange("about_me")}
                    rows={4}
                  />

                  {/*
                    Writing style is structured now, so it saves through its own
                    endpoint rather than riding along with "Looks great". The
                    extracted description still round-trips untouched via `form`.
                  */}
                  <CommunicationStylePanel />
                </div>
              </>
            )}
          </DialogBody>

          <DialogFooter>
            {onFirstStep ? (
              <Button
                variant="tertiary"
                onClick={() => openDomainPrompt()}
                disabled={busy}
              >
                Run onboarding again
              </Button>
            ) : (
              <Button
                variant="tertiary"
                onClick={() => setStep(0)}
                disabled={busy}
              >
                Back
              </Button>
            )}

            <span className={styles.footerSpacer} />

            <Button variant="secondary" onClick={reviewLater} disabled={busy}>
              Review later
            </Button>

            {onFirstStep ? (
              <Button
                variant="primary"
                onClick={() => setStep(1)}
                disabled={busy || !form.company_name}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleApply}
                loading={applying}
                disabled={busy || !form.company_name}
              >
                Looks great
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
